// src/components/pages/configuratoin/Configuration.js
import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Paper, 
  Button, 
  Divider, 
  Grid, 
  Chip,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert
} from '@mui/material';
import { 
  CreditCard, 
  Calendar, 
  AlertCircle, 
  CheckCircle, 
  RefreshCw,
  XCircle
} from 'lucide-react';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import { HEALTH_GREEN } from '../../Login/utils/mockData';
import { useSubscription } from '../../../contexts/context/SubscriptionContext';
import agthealthService from '../../../services/agthealthService';

const Configuration = () => {
  const { 
    isPro, 
    status, 
    trialActive, 
    trialEnd, 
    currentPeriodEnd,
    refreshStatus
  } = useSubscription();
  
  const [loading, setLoading] = useState(false);
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelLoading, setCancelLoading] = useState(false);
  const [cancelError, setCancelError] = useState('');
  const [refreshing, setRefreshing] = useState(false);
  
  // Función para formatear fechas
  const formatDate = (dateString) => {
    if (!dateString) return 'No disponible';
    try {
      const date = new Date(dateString);
      return format(date, "d 'de' MMMM, yyyy", { locale: es });
    } catch (error) {
      console.error('Error al formatear fecha:', error);
      return 'Fecha inválida';
    }
  };
  
  // Función para actualizar estado de suscripción
  const handleRefreshStatus = async () => {
    try {
      setRefreshing(true);
      await refreshStatus();
    } catch (error) {
      console.error('Error al actualizar estado de suscripción:', error);
    } finally {
      setRefreshing(false);
    }
  };
  
  // Función para abrir diálogo de cancelación
  const handleOpenCancelDialog = () => {
    setCancelDialogOpen(true);
    setCancelError('');
  };
  
  // Función para cerrar diálogo de cancelación
  const handleCloseCancelDialog = () => {
    setCancelDialogOpen(false);
  };
  
  // Función para cancelar suscripción
  const handleCancelSubscription = async () => {
    try {
      setCancelLoading(true);
      setCancelError('');
      
      const response = await agthealthService.cancelSubscription();
      
      if (response.success) {
        setCancelDialogOpen(false);
        // Actualizar estado de suscripción después de cancelar
        await refreshStatus();
      } else {
        setCancelError(response.message || 'Error al cancelar la suscripción');
      }
    } catch (error) {
      console.error('Error al cancelar suscripción:', error);
      setCancelError(error.response?.data?.message || 'Error al cancelar la suscripción');
    } finally {
      setCancelLoading(false);
    }
  };
  
  // Función para obtener chip de estado
  const getStatusChip = () => {
    if (trialActive) {
      return <Chip 
        label="Período de prueba" 
        color="primary" 
        size="small" 
        icon={<Calendar size={16} />} 
      />;
    }
    
    switch(status) {
      case 'active':
        return <Chip 
          label="Activa" 
          sx={{ backgroundColor: HEALTH_GREEN, color: 'white' }} 
          size="small" 
          icon={<CheckCircle size={16} />} 
        />;
      case 'past_due':
        return <Chip 
          label="Pago pendiente" 
          color="warning" 
          size="small" 
          icon={<AlertCircle size={16} />} 
        />;
      case 'canceled':
        return <Chip 
          label="Cancelada" 
          color="error" 
          size="small" 
          icon={<XCircle size={16} />} 
        />;
      default:
        return <Chip 
          label={status || "Desconocido"} 
          color="default" 
          size="small" 
        />;
    }
  };
  
  // Función para iniciar nueva suscripción
  const handleStartNewSubscription = async () => {
    try {
      setLoading(true);
      const response = await agthealthService.createSubscriptionCheckout();
      
      if (response.success && response.data.url) {
        window.location.href = response.data.url;
      } else {
        throw new Error('No se pudo crear la sesión de checkout');
      }
    } catch (error) {
      console.error('Error al iniciar suscripción:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box sx={{ maxWidth: 800, mx: 'auto', py: 2 }}>
      <Typography variant="h5" fontWeight="bold" sx={{ mb: 3 }}>
        Gestión de Suscripción
      </Typography>
      
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
          <Typography variant="h6">
            Estado de Suscripción
          </Typography>
          <Button
            startIcon={<RefreshCw size={16} />}
            size="small"
            onClick={handleRefreshStatus}
            disabled={refreshing}
          >
            {refreshing ? 'Actualizando...' : 'Actualizar'}
          </Button>
        </Box>
        
        <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
          <Typography variant="body1" sx={{ mr: 2 }}>
            Estado:
          </Typography>
          {getStatusChip()}
        </Box>
        
        <Grid container spacing={2}>
          {trialActive && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Fin del período de prueba:
              </Typography>
              <Typography variant="body1">
                {formatDate(trialEnd)}
              </Typography>
            </Grid>
          )}
          
          {currentPeriodEnd && (
            <Grid item xs={12} sm={6}>
              <Typography variant="body2" color="text.secondary">
                Próxima facturación:
              </Typography>
              <Typography variant="body1">
                {formatDate(currentPeriodEnd)}
              </Typography>
            </Grid>
          )}
        </Grid>
      </Paper>
      
      <Paper elevation={1} sx={{ p: 3, mb: 4, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Detalles del Plan
        </Typography>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Plan actual:
          </Typography>
          <Typography variant="body1" fontWeight="medium">
            AGT Health Pro
          </Typography>
        </Box>
        
        <Box sx={{ mb: 2 }}>
          <Typography variant="body2" color="text.secondary">
            Precio:
          </Typography>
          <Typography variant="body1">
            $10 USD / mes
          </Typography>
        </Box>
        
        <Divider sx={{ my: 2 }} />
        
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box>
            <Typography variant="body2" color="text.secondary">
              Método de pago:
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', mt: 0.5 }}>
              <CreditCard size={16} style={{ marginRight: 8 }} />
              <Typography variant="body1">
                Tarjeta terminada en ****
              </Typography>
            </Box>
          </Box>
          
          <Button 
            variant="outlined" 
            size="small"
            disabled={!isPro || status !== 'active'}
          >
            Actualizar
          </Button>
        </Box>
      </Paper>
      
      <Paper elevation={1} sx={{ p: 3, borderRadius: 2 }}>
        <Typography variant="h6" sx={{ mb: 2 }}>
          Acciones
        </Typography>
        
        {isPro && status === 'active' ? (
          <Button 
            variant="outlined" 
            color="error"
            onClick={handleOpenCancelDialog}
          >
            Cancelar Suscripción
          </Button>
        ) : status === 'past_due' ? (
          <Box>
            <Alert severity="warning" sx={{ mb: 2 }}>
              Tu último pago no pudo ser procesado. Por favor actualiza tu método de pago para continuar con tu suscripción.
            </Alert>
            <Button 
              variant="contained"
              onClick={handleStartNewSubscription}
              disabled={loading}
              sx={{ bgcolor: HEALTH_GREEN, '&:hover': { bgcolor: '#0e8a5f' } }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Actualizar método de pago'}
            </Button>
          </Box>
        ) : (
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              No tienes una suscripción activa actualmente.
            </Typography>
            <Button 
              variant="contained"
              onClick={handleStartNewSubscription}
              disabled={loading}
              sx={{ bgcolor: HEALTH_GREEN, '&:hover': { bgcolor: '#0e8a5f' } }}
            >
              {loading ? <CircularProgress size={24} color="inherit" /> : 'Suscribirse ahora'}
            </Button>
          </Box>
        )}
      </Paper>
      
      {/* Diálogo de confirmación para cancelar suscripción */}
      <Dialog
        open={cancelDialogOpen}
        onClose={handleCloseCancelDialog}
        aria-labelledby="cancel-dialog-title"
      >
        <DialogTitle id="cancel-dialog-title">
          Cancelar Suscripción
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            ¿Estás seguro de que deseas cancelar tu suscripción a AGT Health Pro?
            Perderás acceso a todas las funcionalidades premium al final del período actual.
          </DialogContentText>
          {cancelError && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {cancelError}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCancelDialog} disabled={cancelLoading}>
            Volver
          </Button>
          <Button 
            onClick={handleCancelSubscription} 
            color="error" 
            disabled={cancelLoading}
            autoFocus
          >
            {cancelLoading ? <CircularProgress size={24} /> : 'Confirmar Cancelación'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Configuration;